import { withFormik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from "react";
import { useTranslation, withTranslation } from 'react-i18next';
import { putFamilyHistory, removeFamilyHistoryAPI } from "../../../api/medicalRecord";
import { AuditModal, CreateAudit, CreateAuditModal } from "../../../components/AuditTrail/AuditModal";
import { ExpandableContent } from "../../../components/CustomControls/ExpandableContent";
import { FamilyHistory } from "../../../components/MedicalRecords/FamilyHistory";
import { FamilyHistoryForm } from "../../../components/MedicalRecords/Forms/FamilyHistoryForm";
import { getMedicalRecordsSchema } from "../../../components/Validations/FormValidationSchema";
import { AuditTrail } from "../../Admin/AuditTrail";
import { useFamilyHistories, useFamilyHistory } from "../Hooks/useFamilyHistory";
import { ModalConfirm } from '../../../components/Modals/ModalConfirm';
import { Success } from '../../../components/Notifications/Notifications';
import { HandleApiError } from '../../../components/Notifications/APIErrorHandler';
import { ModalSpinner } from '../../../components/Utils/Loaders';

export const FamilyHistoryContainer = (props) => {
    const [content, setContent] = useState(null);
    const { entryStatus, entryId, expandedAll } = props;
    const [data, query, totalSize, isLoading, handleTableChange, setReload] = useFamilyHistories(null, entryId);
    const { t } = useTranslation();

    useEffect(() => {
        if (props.contentToReload?.contentName === "familyHistory")
            setReload(true);
    }, [props.contentToReload]);

    const handleEdit = (id) => {
        setContent(<FamilyHistoryEditContainer
            entryId={entryId}
            id={id}
            onSubmit={handleSubmit}
            onCancel={handleClose}
        />);
    }

    const handleSubmit = async (data) => {

        if (entryStatus === "Published" || entryStatus === "AutoPublished") {
            setContent(<CreateAuditModal
                onClose={() => setContent(null)}
                onSave={(reason, comments) => onConfirmSubmit(data, reason, comments)}
            />)
        }
        else {
            onConfirmSubmit(data);
        }
    }

    const onConfirmSubmit = async (data, reason, comments) => {
        try {            
            if (data && data.familyHistories && data.familyHistories.length > 0) {
                let familyHistory = data.familyHistories[0];
                if (reason) {
                    familyHistory.auditReason = reason;
                    familyHistory.auditComments = comments;
                }

                await putFamilyHistory(entryId, familyHistory.id, familyHistory);
                setContent(null);
                setReload(true);
            }
        }
        catch (error) {
            console.log(error);
        }
    }

    const handleClose = () => {
        setContent(null);
    }

    const handleAuditTrail = (entityId) => {
        setContent(
            <AuditModal
                onClose={() => { setContent(null); }}
                title={t("auditTrail.audit")}
            >
                <AuditTrail entity="FamilyHistory" entityId={entityId} />
            </AuditModal>
        )
    }

    //#region Delete item with audit
    const handleRemove = (id) => {
        setContent(
            <ModalConfirm
                onConfirm={() => confirmRemoveItem(id)}
                onCancel={() => setContent(null)}
                title={t("medicalRecordsNotifications.item_delete_title")}
                description={t("medicalRecordsNotifications.item_delete_description")}
                confirmStyle="danger"
            />);
    }
    const confirmRemoveItem = (id) => {
        if (entryStatus === "Published" || entryStatus === "AutoPublished") {
            setContent(<CreateAuditModal
                onClose={() => setContent(null)}
                onSave={(reason, comments) => _removeItemAudited(id, reason, comments)}
            />)
        }
        else {
            _removeItemAudited(id, null, null);
        }
    }
    const _removeItemAudited = async (id, reason, comments) => {
        try {
            setContent(<ModalSpinner isShowing={true} hide={null} />);
            await removeFamilyHistoryAPI(entryId, id, reason?.code, comments);
            setReload(true);
            setContent(null);
            Success("medicalRecordsNotifications.item_DeletedSuccessfully");
        }
        catch (error) {
            setContent(null);
            console.log(error);
            HandleApiError(error);
        }
    }
    //#endregion

    if (totalSize === 0)
        return null;

    return (
        <ExpandableContent title={t("medicalRecords.familyHistory.title")} expandedAll={expandedAll} content="familyHistory">
            {content}
            <FamilyHistory
                data={data}
                entryId={entryId}
                withCard={false}
                query={query}
                totalSize={totalSize}
                isLoading={isLoading}
                onTableChange={handleTableChange}
                onEdit={handleEdit}
                onAuditTrail={handleAuditTrail}
                onRemove={handleRemove}
            />
        </ExpandableContent>
    );
}

export const FamilyHistoryCreateContainer = (props) => {
    const { entryStatus, entryId, onSubmit } = props;
    const [, , create] = useFamilyHistory(entryId);
    const [content, setContent] = useState(null);

    const handleSubmit = async (data) => {

        if (entryStatus === "Published" || entryStatus === "AutoPublished") {
            setContent(<CreateAudit
                onClose={() => setContent(null)}
                onSave={(reason, comments) => onConfirmSubmit(data, reason, comments)}
            />)
        }
        else {
            onConfirmSubmit(data);
        }
    }

    const onConfirmSubmit = async (data, reason, comments) => {
        try {
            if (reason) {
                data.auditReason = reason;
                data.auditComments = comments;
            }
            console.log(data)
            await create(entryId, data);
            setContent(null);
            onSubmit("familyHistory")
        }
        catch (error) {
            console.log(error);
        }
    }

    const handleCancel = () => { props.hide() }

    return (
        <>
            {
                content ??
                <FamilyHistoryContainerFormik
                    onSubmit={handleSubmit}
                    onCancel={handleCancel}
                />
            }
        </>

    );
}

const FamilyHistoryEditContainer = (props) => {
    const { entryId, id, onSubmit } = props;
    const [data, isLoading] = useFamilyHistory(entryId, id);

    if (!data || isLoading)
        return null;

    return (
        <FamilyHistoryContainerFormik
            onSubmit={onSubmit}
            onCancel={props.onCancel}
            familyHistory={data}
        />
    );
}

const FamilyHistoryContainerFormik = withTranslation()(withFormik({
    mapPropsToValues: (props) => (props.familyHistory && { familyHistories: [props.familyHistory] }),

    validationSchema: getMedicalRecordsSchema().familyHistory,

    handleSubmit: (values, formikBag) => {
        formikBag.props.onSubmit(values);
    },

    displayName: 'FamilyHistoryForm',

})(FamilyHistoryForm));

FamilyHistoryContainerFormik.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    familyHistory: PropTypes.object.isRequired,
};